// services/api.js
import axios from 'axios'

const API_BASE_URL = `${process.env.VUE_APP_API_BASE_URL || '/'}api/v1/`

class ApiClient {
    constructor() {
        this.client = axios.create({
            baseURL: API_BASE_URL,
            headers: {
                'Content-Type': 'application/json'
            }
        })

        // Request interceptor to add auth token
        this.client.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )

        // Response interceptor for error handling
        this.client.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    // Only redirect if NOT already on login page and NOT a login request
                    const isLoginRequest = error.config?.url?.includes('auth/login')
                    const isOnLoginPage = window.location.pathname === '/login'

                    if (!isLoginRequest && !isOnLoginPage) {
                        // Hard redirect to login on auth errors - bypasses Vue Router
                        localStorage.removeItem('token')
                        localStorage.removeItem('user')
                        window.location.href = '/login'
                    }
                    // If it's a login request or we're on login page, just reject without redirect
                }
                return Promise.reject(error)
            }
        )
    }

    // Generic GET request
    async get(endpoint, params = {}) {
        try {
            const response = await this.client.get(endpoint, { params })
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    }

    // Generic POST request
    async post(endpoint, data = {}) {
        try {
            const response = await this.client.post(endpoint, data)
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    }

    // Generic PUT request
    async put(endpoint, data = {}) {
        try {
            const response = await this.client.put(endpoint, data)
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    }

    // Generic DELETE request
    async delete(endpoint) {
        try {
            const response = await this.client.delete(endpoint)
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    }

    // Fetch a binary resource (e.g. a PDF) as a Blob, with the auth header
    // attached. Opening the endpoint in a plain new tab would omit the JWT.
    async getBlob(endpoint, params = {}) {
        const response = await this.client.get(endpoint, { params, responseType: 'blob' })
        return response.data
    }

    // Open a PDF endpoint in a new tab (auth-aware via getBlob).
    // The tab is opened synchronously BEFORE the await so it keeps the user
    // gesture (otherwise pop-up blockers silently swallow it). If the popup is
    // blocked we fall back to a same-document download.
    async openPdf(endpoint) {
        const win = window.open('', '_blank')
        try {
            const blob = await this.getBlob(endpoint)
            const url = URL.createObjectURL(blob)
            if (win && !win.closed) {
                win.location = url
            } else {
                const a = document.createElement('a')
                a.href = url
                a.download = `${endpoint.split('/').pop()}.pdf`
                document.body.appendChild(a)
                a.click()
                a.remove()
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000)
        } catch (e) {
            if (win && !win.closed) win.close()
            throw e
        }
    }

    // File upload with FormData
    async uploadFile(endpoint, file, additionalData = {}) {
        try {
            const formData = new FormData()
            formData.append('file', file)

            // Add any additional data
            Object.keys(additionalData).forEach(key => {
                formData.append(key, additionalData[key])
            })

            const response = await this.client.post(endpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    }

    // Authentication specific methods
    async login(credentials) {
        return this.post('auth/login', credentials)
    }

    async logout() {
        try {
            return await this.post('auth/logout')
        } catch (error) {
            console.error('Logout API error:', error)
            // Return a success response anyway since we're going to clear the client state
            return { success: true }
        }
    }

    // Generic fetch method (similar to your current fetchData)
    async fetch(endpoint, payload = {}, options = {}) {
        const { method = 'GET', params, ...restOptions } = options

        try {
            let response

            switch (method.toUpperCase()) {
                case 'GET':
                    response = await this.client.get(endpoint, { params: payload, ...restOptions })
                    break
                case 'POST':
                    response = await this.client.post(endpoint, payload, restOptions)
                    break
                case 'PUT':
                    response = await this.client.put(endpoint, payload, restOptions)
                    break
                case 'DELETE':
                    response = await this.client.delete(endpoint, { ...restOptions, data: payload })
                    break
                default:
                    throw new Error(`Unsupported HTTP method: ${method}`)
            }

            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    }

    // Error handling
    handleError(error) {
        const errorMessage = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            'An unexpected error occurred'

        return {
            message: errorMessage,
            status: error.response?.status,
            data: error.response?.data
        }
    }
}

// Create and export a singleton instance
const apiClient = new ApiClient()
export default apiClient

// Also export the class for testing or multiple instances
export { ApiClient }
